import { Box, type ContainerProps, SlideFade } from '@chakra-ui/react';
import React, { FC, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { IQuizStepData, QuizFormStep } from './QuizStepForm';

interface IStepComponentProps {
  currentStep: number;
  onNext: () => void;
  onPrev: () => void;
}

type TQuizStep<T extends Record<string, any> = Record<string, any>> = {
  shouldSkip?: (formData: T) => boolean;
} & (
  | {
      isDefaultQuizFormStep: true;
      stepData: IQuizStepData;
      CustomStepComponent?: never;
    }
  | {
      isDefaultQuizFormStep?: false;
      CustomStepComponent: FC<IStepComponentProps>;
      stepData?: never;
    }
);

interface ISlider<T extends Record<string, any> = Record<string, any>> extends ContainerProps {
  steps: TQuizStep<T>[];
  methods?: ReturnType<typeof useForm<T>>;
}

const Slider = <T extends Record<string, any> = Record<string, any>>({
  steps,
  methods,
  ...rest
}: ISlider<T>) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [slideIn, setSlideIn] = useState(true);
  const [slideDirection, setSlideDirection] = useState('left');

  const stepsComponents = useMemo(
    () =>
      steps.map(({ CustomStepComponent, isDefaultQuizFormStep, stepData }) => {
        if (isDefaultQuizFormStep) {
          const StepItem = (props: IStepComponentProps) => (
            <QuizFormStep
              {...props}
              {...stepData}
            />
          );

          return StepItem;
        }

        return CustomStepComponent;
      }),
    [steps],
  );

  const handleNext = useMemo(
    () => () => {
      let nextStep = currentStep + 1;

      const getValues = methods?.getValues;

      while (nextStep < steps.length && getValues && steps[nextStep]?.shouldSkip?.(getValues())) {
        nextStep++;
      }

      if (nextStep < steps.length) {
        setSlideIn(false);
        setSlideDirection('left');
        setTimeout(() => {
          setCurrentStep(nextStep);
          setSlideIn(true);
        }, 300);
      }
    },
    [currentStep, steps.length, methods],
  );

  const handlePrev = useMemo(
    () => () => {
      let prevStep = currentStep - 1;

      const getValues = methods?.getValues;

      while (prevStep >= 0 && getValues && steps[prevStep]?.shouldSkip?.(getValues())) {
        prevStep--;
      }

      if (prevStep >= 0) {
        setSlideIn(false);
        setSlideDirection('right');
        setTimeout(() => {
          setCurrentStep(prevStep);
          setSlideIn(true);
        }, 300);
      }
    },
    [currentStep, steps, methods],
  );
  const Component = stepsComponents[currentStep];

  return (
    <Box
      overflow='hidden'
      {...rest}
    >
      <SlideFade
        in={slideIn}
        offsetX={slideDirection === 'right' ? 50 : -50}
        offsetY={0}
        style={{ zIndex: 10 }}
      >
        {Component && (
          <Component
            currentStep={currentStep}
            onNext={handleNext}
            onPrev={handlePrev}
          />
        )}
      </SlideFade>
    </Box>
  );
};

export type { TQuizStep, IStepComponentProps };
export { Slider };
